"use client";

import { resizeImageUrl } from "@lib/util/images";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useEffect, useState } from "react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import { Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

type ImageGalleryProps = {
  images: { url: string }[];
  thumbsSwiper: any;
};

const ImagePreviewGallery = ({ images, thumbsSwiper }: ImageGalleryProps) => {
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [direction, setDirection] = useState<"next" | "prev">("next");

  const handleOpenPreview = (index: number) => setCurrentIndex(index);
  const handleClosePreview = () => setCurrentIndex(null);

  const handleNext = () => {
    if (currentIndex === null) return;
    setDirection("next");
    setCurrentIndex((currentIndex + 1) % images.length);
  };

  const handlePrev = () => {
    if (currentIndex === null) return;
    setDirection("prev");
    setCurrentIndex((currentIndex - 1 + images.length) % images.length);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClosePreview();
    };

    if (currentIndex !== null) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIndex]);

  return (
    <div className="w-full max-w-lg mx-auto">
      <Swiper
        modules={[Navigation, Thumbs]}
        spaceBetween={10}
        slidesPerView={1}
        navigation
        thumbs={{ swiper: thumbsSwiper }}
        loop
        className="border rounded-lg max-w-96 max-h-96"
      >
        {images.map((image, index) => (
          <SwiperSlide key={index} onClick={() => handleOpenPreview(index)}>
            <Image
              src={resizeImageUrl(image.url, 384, 384)}
              alt={`image-${index}`}
              width={384}
              height={384}
              className="w-full h-full object-contain cursor-pointer"
              fetchPriority="high"
              priority
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <AnimatePresence>
        {currentIndex !== null && (
          <motion.div
            className="fixed inset-0 z-50 bg-black bg-opacity-80 flex justify-center items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button onClick={handleClosePreview} className="absolute top-4 right-4 text-white text-xl">
              ✕
            </button>
            <button onClick={handlePrev} className="absolute left-4 text-white text-3xl">
              ❮
            </button>

            <motion.div
              key={currentIndex}
              initial={{ x: direction === "next" ? 100 : -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: direction === "next" ? -100 : 100, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Zoom>
                <Image
                  src={images[currentIndex].url}
                  alt={`preview-${currentIndex}`}
                  width={800}
                  height={800}
                  className="max-h-[80vh] object-contain"
                />
              </Zoom>
            </motion.div>

            <button onClick={handleNext} className="absolute right-4 text-white text-3xl">
              ❯
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImagePreviewGallery;
